import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Input, Checkbox, Modal } from 'antd';
import { SaveOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css';
import api from '../config/api';
import './common.css';
import './Settings.css';
import Navigation from './Navigation';

function Settings() {
    const navigate = useNavigate();
    const [settings, setSettings] = useState({
        amazon_refresh_token: '',
        amazon_client_id: '',
        amazon_client_secret: '',
        amazon_seller_id: '',
        amazon_initial_forecast: 0,
        amazon_alpha: 0.30,
        amazon_buffer: 0,
        // Profit Calculation
        search_amazonReferralFee: 15.0,
        search_rewardsRateOnPurchases: 0.0,
        search_marginalTaxRate: 0.0,
        search_salesTax: 0.0,
        search_baseMargin: 10.0,
        search_amazonNonPaymentRisk: 0.0,
        search_minimumMarginPercentage: 5.0,
        search_monthlySales: 3,
        search_amazonBuffer: 15,
        search_fbaBuffer: 10,
        // Product Filters
        search_filterItemQuantity: 0,
        search_filterEbayMinPrice: 0.0,
        search_filterEbayMaxPrice: 9999.0,
        search_filterSellerFeedback: 0.0,
        search_filterOutLowInventory: false,
        search_inventoryThreshold: 0,
        // API Configuration
        search_scrapingDogApiKey: '',
        search_googleSpreadsheetId: '',
        search_googleSheetsApiKey: '',
        search_googleSheetsSellersSheetName: 'Sellers',
        search_googleSheetsBrandsSheetName: 'Brands',
        // Keepa API
        keepa_api_key: '',
        // General
        search_countryId: 'US',
        search_cache_days: 1,
        // Search Runner
        search_runner_num_threads: 10
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [activeTab, setActiveTab] = useState('settings');
    const [apiCredentials, setApiCredentials] = useState([]);
    const [credentialsLoading, setCredentialsLoading] = useState(false);
    const [credentialsSaving, setCredentialsSaving] = useState(false);
    const [credentialsMessage, setCredentialsMessage] = useState({ type: '', text: '' });
    const [newCredential, setNewCredential] = useState({
        refresh_token: '',
        client_id: '',
        client_secret: '',
        seller_id: '',
        country: '',
        is_default: false
    });

    const fetchCredentials = useCallback(async () => {
        setCredentialsLoading(true);
        setCredentialsMessage({ type: '', text: '' });

        try {
            const response = await api.get('/api/sp-api-credentials');
            if (response.data.success) {
                setApiCredentials(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching SP API credentials:', error);
            if (error.response?.status === 401) {
                navigate('/');
            } else {
                setCredentialsMessage({
                    type: 'error',
                    text: error.response?.data?.error || 'Failed to load SP API credentials'
                });
            }
        } finally {
            setCredentialsLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (activeTab === 'spapi') {
            fetchCredentials();
        }
    }, [activeTab, fetchCredentials]);

    const handleNewCredentialChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewCredential(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleExistingCredentialChange = (index, field, value) => {
        setApiCredentials(prev => prev.map((item, idx) => idx === index ? { ...item, [field]: value } : item));
    };

    const addCredential = async () => {
        setCredentialsSaving(true);
        setCredentialsMessage({ type: '', text: '' });

        try {
            const response = await api.post('/api/sp-api-credentials', newCredential);
            if (response.data.success) {
                setApiCredentials(prev => [...prev, response.data.data[0] || response.data.data]);
                setNewCredential({
                    refresh_token: '',
                    client_id: '',
                    client_secret: '',
                    seller_id: '',
                    country: '',
                    is_default: false
                });
                setCredentialsMessage({ type: 'success', text: 'Credential added successfully' });
            }
        } catch (error) {
            console.error('Error adding SP API credential:', error);
            setCredentialsMessage({
                type: 'error',
                text: error.response?.data?.error || 'Failed to add SP API credential'
            });
        } finally {
            setCredentialsSaving(false);
        }
    };

    const saveCredentialUpdate = async (index) => {
        const credential = apiCredentials[index];
        const updates = {
            refresh_token: credential.refresh_token,
            client_secret: credential.client_secret,
            seller_id: credential.seller_id,
            country: credential.country,
            is_default: credential.is_default
        };

        setCredentialsSaving(true);
        setCredentialsMessage({ type: '', text: '' });

        try {
            const response = await api.put(`/api/sp-api-credentials/${credential.client_id}`, updates);
            if (response.data.success) {
                const updated = response.data.data[0] || response.data.data;
                setApiCredentials(prev => prev.map((item, idx) => idx === index ? updated : item));
                setCredentialsMessage({ type: 'success', text: 'Credential updated successfully' });
            }
        } catch (error) {
            console.error('Error updating SP API credential:', error);
            setCredentialsMessage({
                type: 'error',
                text: error.response?.data?.error || 'Failed to update SP API credential'
            });
        } finally {
            setCredentialsSaving(false);
        }
    };

    const deleteCredential = async (clientId) => {
        if (!window.confirm('Delete this SP API credential set?')) {
            return;
        }

        setCredentialsSaving(true);
        setCredentialsMessage({ type: '', text: '' });

        try {
            const response = await api.delete(`/api/sp-api-credentials/${clientId}`);
            if (response.data.success) {
                setApiCredentials(prev => prev.filter(item => item.client_id !== clientId));
                setCredentialsMessage({ type: 'success', text: 'Credential deleted successfully' });
            }
        } catch (error) {
            console.error('Error deleting SP API credential:', error);
            setCredentialsMessage({
                type: 'error',
                text: error.response?.data?.error || 'Failed to delete SP API credential'
            });
        } finally {
            setCredentialsSaving(false);
        }
    };

    const validateCredential = async (clientId) => {
        setCredentialsSaving(true);
        setCredentialsMessage({ type: '', text: '' });

        try {
            const response = await api.get(`/api/sp-api-credentials/${clientId}/validate`);
            const title = `Validate SP-API credentials: ${clientId}`;

            if (response.data.success) {
                const valid = response.data.data?.valid;
                if (valid) {
                    Modal.success({
                        title,
                        content: `Credentials for "${clientId}" are valid.`
                    });
                } else {
                    Modal.error({
                        title,
                        content: `Credentials for "${clientId}" are invalid. Please check the refresh token, client secret, seller ID, and country.`
                    });
                }
            } else {
                Modal.error({
                    title,
                    content: response.data.error || `Failed to validate credentials for "${clientId}".`
                });
            }
        } catch (error) {
            console.error('Error validating SP API credential:', error);
            const errorMessage = error.response?.data?.error || error.message || `Failed to validate credentials for "${clientId}".`;
            Modal.error({
                title: `Validate SP-API credentials: ${clientId}`,
                content: errorMessage
            });
        } finally {
            setCredentialsSaving(false);
        }
    };

    const fetchSettings = useCallback(async () => {
        try {
            const response = await api.get('/api/settings');

            if (response.data.success) {
                const data = response.data.data;
                setSettings({
                    amazon_refresh_token: data.amazon_refresh_token || '',
                    amazon_client_id: data.amazon_client_id || '',
                    amazon_client_secret: data.amazon_client_secret || '',
                    amazon_seller_id: data.amazon_seller_id || '',
                    amazon_initial_forecast: data.amazon_initial_forecast || 0,
                    amazon_alpha: parseFloat(data.amazon_alpha) || 0.30,
                    amazon_buffer: data.amazon_buffer || 0,
                    // Profit Calculation
                    search_amazonReferralFee: parseFloat(data.search_amazonReferralFee) || 15.0,
                    search_rewardsRateOnPurchases: parseFloat(data.search_rewardsRateOnPurchases) || 0.0,
                    search_marginalTaxRate: parseFloat(data.search_marginalTaxRate) || 0.0,
                    search_salesTax: parseFloat(data.search_salesTax) || 0.0,
                    search_baseMargin: parseFloat(data.search_baseMargin) || 10.0,
                    search_amazonNonPaymentRisk: parseFloat(data.search_amazonNonPaymentRisk) || 0.0,
                    search_minimumMarginPercentage: parseFloat(data.search_minimumMarginPercentage) || 5.0,
                    search_monthlySales: parseInt(data.search_monthlySales) || 3,
                    search_amazonBuffer: parseInt(data.search_amazonBuffer) || 15,
                    search_fbaBuffer: parseInt(data.search_fbaBuffer) || 10,
                    // Product Filters
                    search_filterItemQuantity: parseInt(data.search_filterItemQuantity) || 0,
                    search_filterEbayMinPrice: parseFloat(data.search_filterEbayMinPrice) || 0.0,
                    search_filterEbayMaxPrice: parseFloat(data.search_filterEbayMaxPrice) || 9999.0,
                    search_filterSellerFeedback: parseFloat(data.search_filterSellerFeedback) || 0.0,
                    search_filterOutLowInventory: data.search_filterOutLowInventory === true || data.search_filterOutLowInventory === 'true' || false,
                    search_inventoryThreshold: parseInt(data.search_inventoryThreshold) || 0,
                    // API Configuration
                    search_scrapingDogApiKey: data.search_scrapingDogApiKey || '',
                    search_googleSpreadsheetId: data.search_googleSpreadsheetId || '',
                    search_googleSheetsApiKey: data.search_googleSheetsApiKey || '',
                    search_googleSheetsSellersSheetName: data.search_googleSheetsSellersSheetName || 'Sellers',
                    search_googleSheetsBrandsSheetName: data.search_googleSheetsBrandsSheetName || 'Brands',
                    // Keepa API
                    keepa_api_key: data.keepa_api_key || '',
                    // General
                    search_countryId: data.search_countryId || 'US',
                    search_cache_days: parseInt(data.search_cache_days) || 1,
                    // Search Runner
                    search_runner_num_threads: parseInt(data.search_runner_num_threads) || 5,
                });
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            if (error.response?.status === 401) {
                navigate('/');
            } else {
                setMessage({
                    type: 'error',
                    text: error.response?.data?.error || 'Failed to load settings'
                });
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked :
                name === 'amazon_initial_forecast' || name === 'amazon_buffer' ||
                name === 'search_monthlySales' || name === 'search_amazonBuffer' ||
                name === 'search_fbaBuffer' || name === 'search_filterItemQuantity' ||
                name === 'search_inventoryThreshold' || name === 'search_cache_days' || name === 'search_runner_num_threads' ? parseInt(value) || 0 :
                name === 'amazon_alpha' ||
                name === 'search_amazonReferralFee' || name === 'search_rewardsRateOnPurchases' ||
                name === 'search_marginalTaxRate' || name === 'search_salesTax' ||
                name === 'search_baseMargin' || name === 'search_amazonNonPaymentRisk' ||
                name === 'search_minimumMarginPercentage' ||
                name === 'search_filterEbayMinPrice' ||
                name === 'search_filterEbayMaxPrice' || name === 'search_filterSellerFeedback' ? parseFloat(value) || 0 :
                value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            // Only send non-empty fields
            const dataToSend = {};
            Object.keys(settings).forEach(key => {
                if (settings[key] !== '' && settings[key] !== null) {
                    dataToSend[key] = settings[key];
                }
            });

            const response = await api.put(
                '/api/settings',
                dataToSend
            );

            if (response.data.success) {
                setMessage({
                    type: 'success',
                    text: 'Settings saved successfully'
                });
                // Refresh settings to get updated values
                await fetchSettings();
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            setMessage({
                type: 'error',
                text: error.response?.data?.error ||
                    (error.response?.data?.errors?.[0]?.msg) ||
                    'Failed to save settings'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete all app settings? This will reset to defaults for all users. This action cannot be undone.')) {
            return;
        }

        try {
            const response = await api.delete('/api/settings');

            if (response.data.success) {
                setMessage({
                    type: 'success',
                    text: 'Settings deleted successfully'
                });
                // Reset to defaults
                setSettings({
                    amazon_refresh_token: '',
                    amazon_client_id: '',
                    amazon_client_secret: '',
                    amazon_seller_id: '',
                    amazon_initial_forecast: 0,
                    amazon_alpha: 0.30,
                    amazon_buffer: 0,
                    search_amazonReferralFee: 15.0,
                    search_rewardsRateOnPurchases: 0.0,
                    search_marginalTaxRate: 0.0,
                    search_salesTax: 0.0,
                    search_baseMargin: 10.0,
                    search_amazonNonPaymentRisk: 0.0,
                    search_minimumMarginPercentage: 5.0,
                    search_filterItemQuantity: 0,
                    search_filterEbayMinPrice: 0.0,
                    search_filterEbayMaxPrice: 9999.0,
                    search_filterSellerFeedback: 0.0,
                    search_filterOutLowInventory: false,
                    search_inventoryThreshold: 0,
                    search_scrapingDogApiKey: '',
                    search_googleSpreadsheetId: '',
                    search_googleSheetsApiKey: '',
                    search_googleSheetsSellersSheetName: 'Sellers',
                    search_googleSheetsBrandsSheetName: 'Brands',
                    search_countryId: 'US',
                    search_cache_days: 1,
                    search_runner_num_threads: 5
                });
            }
        } catch (error) {
            console.error('Error deleting settings:', error);
            setMessage({
                type: 'error',
                text: error.response?.data?.error || 'Failed to delete settings'
            });
        }
    };

    if (loading) {
        return (
            <div className="page-container">
                <Navigation />
                <div className="page-content">
                    <div className="loading">Loading settings...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <Navigation />
            <div className="page-content">
                <div className="page-header">
                    <h2>App Settings</h2>
                </div>

                {settings.updated_by && (
                    <div className="settings-info">
                        Last updated by {settings.updated_by_first_name} {settings.updated_by_last_name} on {new Date(settings.updated_at).toLocaleString()}
                    </div>
                )}

                <Tabs activeKey={activeTab} onChange={setActiveTab} className="settings-tabs">
                    <Tabs.TabPane tab="Settings" key="settings">
                        {message.text && (
                            <div className={`message ${message.type}`}>
                                {message.text}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="settings-form">

                            {/* Forecast Settings Section */}
                            <div className="settings-section">
                                <h3>Forecast Settings</h3>
                                <p className="section-description">
                                    Configure inventory forecasting parameters for Amazon listings (applies to all users).
                                </p>

                                <div className="form-group">
                                    <label htmlFor="amazon_initial_forecast">Initial Forecast</label>
                                    <input
                                        type="number"
                                        id="amazon_initial_forecast"
                                        name="amazon_initial_forecast"
                                        value={settings.amazon_initial_forecast}
                                        onChange={handleChange}
                                        min="0"
                                        placeholder="Enter initial forecast value"
                                    />
                                    <small>The starting forecast value for new products</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="amazon_alpha">Alpha (α)</label>
                                    <input
                                        type="number"
                                        id="amazon_alpha"
                                        name="amazon_alpha"
                                        value={settings.amazon_alpha}
                                        onChange={handleChange}
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        placeholder="Enter alpha value (0-1)"
                                    />
                                    <small>Smoothing factor for exponential smoothing (0.0 - 1.0)</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="amazon_buffer">Amazon Buffer</label>
                                    <input
                                        type="number"
                                        id="amazon_buffer"
                                        name="amazon_buffer"
                                        value={settings.amazon_buffer}
                                        onChange={handleChange}
                                        min="0"
                                        placeholder="Enter buffer value"
                                    />
                                    <small>Buffer for Amazon inventory management</small>
                                </div>
                            </div>

                            {/* Profit Calculation Section */}
                            <div className="settings-section">
                                <h3>Product Sourcing — Profit Calculation</h3>
                                <p className="section-description">
                                    Configure profit calculation variables used when evaluating eBay-to-Amazon arbitrage opportunities.
                                </p>

                                <div className="form-group">
                                    <label htmlFor="search_amazonReferralFee">Amazon Referral Fee (%)</label>
                                    <input type="number" id="search_amazonReferralFee" name="search_amazonReferralFee"
                                        value={settings.search_amazonReferralFee} onChange={handleChange} min="0" max="100" step="0.1" />
                                    <small>Amazon's referral fee percentage (e.g. 15 for 15%)</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_baseMargin">Base Margin ($)</label>
                                    <input type="number" id="search_baseMargin" name="search_baseMargin"
                                        value={settings.search_baseMargin} onChange={handleChange} min="0" max="100" step="0.1" />
                                    <small>Desired base profit margin percentage</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_minimumMarginPercentage">Minimum Margin (%) - <span style={{ color: 'red' }}>Deprecated</span></label>
                                    <input type="number" id="search_minimumMarginPercentage" name="search_minimumMarginPercentage"
                                        value={settings.search_minimumMarginPercentage} onChange={handleChange} min="0" max="100" step="0.1" />
                                    <small>Minimum acceptable profit margin - products below this are flagged - Deprecated (only kept for backward compatibility)</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_salesTax">Sales Tax (%) - <span style={{ color: 'red' }}>Deprecated</span></label>
                                    <input type="number" id="search_salesTax" name="search_salesTax"
                                        value={settings.search_salesTax} onChange={handleChange} min="0" max="100" step="0.1" />
                                    <small>Sales tax rate applied to eBay purchase price - Deprecated (only kept for backward compatibility)</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_marginalTaxRate">Marginal Tax Rate (%) - <span style={{ color: 'red' }}>Deprecated</span></label>
                                    <input type="number" id="search_marginalTaxRate" name="search_marginalTaxRate"
                                        value={settings.search_marginalTaxRate} onChange={handleChange} min="0" max="100" step="0.1" />
                                    <small>Your marginal income tax rate on profits - Deprecated (only kept for backward compatibility)</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_monthlySales">Monthly Sales</label>
                                    <input type="number" id="search_monthlySales" name="search_monthlySales"
                                        value={settings.search_monthlySales} onChange={handleChange} min="0" step="1" />
                                    <small>Expected monthly sales to determine if a product is to be sold</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_amazonBuffer">Amazon Buffer</label>
                                    <input type="number" id="search_amazonBuffer" name="search_amazonBuffer"
                                        value={settings.search_amazonBuffer} onChange={handleChange} min="0" step="1" />
                                    <small>Amazon inventory buffer amount</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_fbaBuffer">FBA Buffer</label>
                                    <input type="number" id="search_fbaBuffer" name="search_fbaBuffer"
                                        value={settings.search_fbaBuffer} onChange={handleChange} min="0" step="1" />
                                    <small>FBA inventory buffer amount</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_rewardsRateOnPurchases">Rewards Rate on Purchases (%)</label>
                                    <input type="number" id="search_rewardsRateOnPurchases" name="search_rewardsRateOnPurchases"
                                        value={settings.search_rewardsRateOnPurchases} onChange={handleChange} min="0" max="100" step="0.1" />
                                    <small>Credit card or rewards return percentage on eBay purchases</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_amazonNonPaymentRisk">Amazon Non-Payment Risk (%)</label>
                                    <input type="number" id="search_amazonNonPaymentRisk" name="search_amazonNonPaymentRisk"
                                        value={settings.search_amazonNonPaymentRisk} onChange={handleChange} min="0" max="100" step="0.1" />
                                    <small>Risk factor for Amazon non-payment or returns</small>
                                </div>
                            </div>

                            {/* Product Filters Section */}
                            <div className="settings-section">
                                <h3>Product Sourcing — Product Filters</h3>
                                <p className="section-description">
                                    Configure filters to automatically exclude eBay listings that don't meet your criteria.
                                </p>

                                <div className="form-group">
                                    <label htmlFor="search_filterItemQuantity">Minimum Item Quantity</label>
                                    <input type="number" id="search_filterItemQuantity" name="search_filterItemQuantity"
                                        value={settings.search_filterItemQuantity} onChange={handleChange} min="0" />
                                    <small>Skip listings with fewer items available than this value (0 = no filter)</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_filterEbayMinPrice">Min eBay Price ($)</label>
                                    <input type="number" id="search_filterEbayMinPrice" name="search_filterEbayMinPrice"
                                        value={settings.search_filterEbayMinPrice} onChange={handleChange} min="0" step="0.01" />
                                    <small>Skip listings priced below this amount</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_filterEbayMaxPrice">Max eBay Price ($)</label>
                                    <input type="number" id="search_filterEbayMaxPrice" name="search_filterEbayMaxPrice"
                                        value={settings.search_filterEbayMaxPrice} onChange={handleChange} min="0" step="0.01" />
                                    <small>Skip listings priced above this amount</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_filterSellerFeedback">Minimum Seller Feedback (%)</label>
                                    <input type="number" id="search_filterSellerFeedback" name="search_filterSellerFeedback"
                                        value={settings.search_filterSellerFeedback} onChange={handleChange} min="0" max="100" step="0.1" />
                                    <small>Skip listings from sellers with feedback below this percentage (0 = no filter)</small>
                                </div>

                                <div className="form-group checkbox-group">
                                    <label htmlFor="search_filterOutLowInventory">
                                        <input type="checkbox" id="search_filterOutLowInventory" name="search_filterOutLowInventory"
                                            checked={settings.search_filterOutLowInventory} onChange={handleChange} />
                                        Filter Out Low Inventory
                                    </label>
                                    <small>Exclude products where Amazon inventory is below the threshold below</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_inventoryThreshold">Inventory Threshold</label>
                                    <input type="number" id="search_inventoryThreshold" name="search_inventoryThreshold"
                                        value={settings.search_inventoryThreshold} onChange={handleChange} min="0"
                                        disabled={!settings.search_filterOutLowInventory} />
                                    <small>Minimum Amazon inventory count required (only active when filter above is enabled)</small>
                                </div>
                            </div>

                            {/* API Configuration Section */}
                            <div className="settings-section">
                                <h3>Product Sourcing — API Configuration</h3>
                                <p className="section-description">
                                    Configure API keys for eBay scraping and Google Sheets integration for restricted seller/brand lists.
                                </p>

                                <div className="form-group">
                                    <label htmlFor="search_scrapingDogApiKey">ScrapingDog API Key</label>
                                    <input type="text" id="search_scrapingDogApiKey" name="search_scrapingDogApiKey"
                                        value={settings.search_scrapingDogApiKey} onChange={handleChange}
                                        placeholder="Enter your ScrapingDog API key" />
                                    <small>Used for scraping eBay search results and product details</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_googleSheetsApiKey">Google Sheets API Key</label>
                                    <input type="text" id="search_googleSheetsApiKey" name="search_googleSheetsApiKey"
                                        value={settings.search_googleSheetsApiKey} onChange={handleChange}
                                        placeholder="Enter your Google Sheets API key" />
                                    <small>Used to fetch restricted sellers and brands from your Google Spreadsheet</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_googleSpreadsheetId">Google Spreadsheet ID</label>
                                    <input type="text" id="search_googleSpreadsheetId" name="search_googleSpreadsheetId"
                                        value={settings.search_googleSpreadsheetId} onChange={handleChange}
                                        placeholder="e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms" />
                                    {settings.search_googleSpreadsheetId && (
                                        <small>
                                            <a
                                                href={`https://docs.google.com/spreadsheets/d/${settings.search_googleSpreadsheetId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Open spreadsheet ↗
                                            </a>
                                        </small>
                                    )}
                                    {!settings.search_googleSpreadsheetId && (
                                        <small>The ID from the Google Sheets URL (between /d/ and /edit)</small>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_googleSheetsSellersSheetName">Sellers Sheet Name</label>
                                    <input type="text" id="search_googleSheetsSellersSheetName" name="search_googleSheetsSellersSheetName"
                                        value={settings.search_googleSheetsSellersSheetName} onChange={handleChange}
                                        placeholder="Sellers" />
                                    <small>Name of the sheet tab containing restricted seller IDs</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_googleSheetsBrandsSheetName">Brands Sheet Name</label>
                                    <input type="text" id="search_googleSheetsBrandsSheetName" name="search_googleSheetsBrandsSheetName"
                                        value={settings.search_googleSheetsBrandsSheetName} onChange={handleChange}
                                        placeholder="Brands" />
                                    <small>Name of the sheet tab containing restricted brand names</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="keepa_api_key">Keepa API Key</label>
                                    <input type="text" id="keepa_api_key" name="keepa_api_key"
                                        value={settings.keepa_api_key} onChange={handleChange}
                                        placeholder="Enter your Keepa API key" />
                                    <small>Used for fetching product data and price history from Keepa</small>
                                </div>
                            </div>

                            {/* General Search Settings Section */}
                            <div className="settings-section">
                                <h3>Product Sourcing — General</h3>
                                <p className="section-description">
                                    General defaults for product sourcing searches.
                                </p>

                                <div className="form-group">
                                    <label htmlFor="search_countryId">Default Country</label>
                                    <select id="search_countryId" name="search_countryId"
                                        value={settings.search_countryId} onChange={handleChange}
                                        style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}>
                                        <option value="US">US — eBay.com / Amazon.com</option>
                                        <option value="DE">DE — eBay.de / Amazon.de</option>
                                        <option value="UK">UK — eBay.co.uk / Amazon.co.uk</option>
                                    </select>
                                    <small>Default marketplace for new searches</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_cache_days">Search Cache Days</label>
                                    <input type="number" id="search_cache_days" name="search_cache_days"
                                        value={settings.search_cache_days} onChange={handleChange}
                                        min="0" placeholder="Enter cache duration in days" />
                                    <small>Number of days to cache search results for faster repeated lookups</small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="search_runner_num_threads">Search Runner Threads</label>
                                    <input type="number" id="search_runner_num_threads" name="search_runner_num_threads"
                                        value={settings.search_runner_num_threads} onChange={handleChange}
                                        min="0" max="50" placeholder="5" />
                                    <small>Maximum number of concurrent search worker processes (default: 5)</small>
                                </div>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Settings'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDelete}
                                >
                                    Delete Settings
                                </button>
                            </div>
                        </form>
                    </Tabs.TabPane>

                    <Tabs.TabPane tab="SP API Credentials" key="spapi">
                        <div className="settings-form">

                            <p className="settings-subtitle">
                                Credentials with green background are the default credentials.
                                These will be used to sync products from ebay to Amazon, so make sure those belong to your Amazon account. 
                                Generally all credential sets (including the default ones) will be used to process sourcing requests.
                                You can click the checkmark button to check if credentials are still valid.
                            </p>
                            {credentialsMessage.text && (
                                <div className={`message ${credentialsMessage.type}`}>
                                    {credentialsMessage.text}
                                </div>
                            )}

                            {credentialsLoading ? (
                                <div className="loading">Loading credentials...</div>
                            ) : (
                                <>
                                    {apiCredentials.length === 0 ? (
                                        <div className="settings-info">No SP API credentials configured.</div>
                                    ) : (
                                        <div className="settings-section">
                                            {apiCredentials.map((credential, index) => (
                                                <div
                                                    className={`credential-card${credential.is_default === 1 || credential.is_default === true ? ' default-credential' : ''}`}
                                                    key={credential.client_id}
                                                >
                                                    <div className="credential-card-header">
                                                        <h4>Credential #{index + 1}</h4>
                                                        <span className="credential-client-id">{credential.client_id}</span>
                                                    </div>

                                                    <div className="form-group">
                                                        <label>Client ID</label>
                                                        <Input value={credential.client_id} readOnly />
                                                    </div>

                                                    <div className="form-group">
                                                        <label>Refresh Token</label>
                                                        <Input
                                                            value={credential.refresh_token || ''}
                                                            onChange={(e) => handleExistingCredentialChange(index, 'refresh_token', e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="form-group">
                                                        <label>Client Secret</label>
                                                        <Input
                                                            value={credential.client_secret || ''}
                                                            onChange={(e) => handleExistingCredentialChange(index, 'client_secret', e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="form-group">
                                                        <label>Seller ID</label>
                                                        <Input
                                                            value={credential.seller_id || ''}
                                                            onChange={(e) => handleExistingCredentialChange(index, 'seller_id', e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="form-group">
                                                        <label>Country</label>
                                                        <Input
                                                            value={credential.country || ''}
                                                            onChange={(e) => handleExistingCredentialChange(index, 'country', e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="form-group checkbox-group">
                                                        <Checkbox
                                                            checked={credential.is_default === 1 || credential.is_default === true}
                                                            onChange={(e) => handleExistingCredentialChange(index, 'is_default', e.target.checked)}
                                                        >
                                                            Set as default
                                                        </Checkbox>
                                                    </div>

                                                    <div className="form-actions credential-actions">
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary"
                                                            disabled={credentialsSaving}
                                                            onClick={() => saveCredentialUpdate(index)}
                                                            aria-label="Save credential"
                                                        >
                                                            <SaveOutlined />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            disabled={credentialsSaving}
                                                            onClick={() => validateCredential(credential.client_id)}
                                                            aria-label="Validate credential"
                                                        >
                                                            <CheckCircleOutlined />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger"
                                                            disabled={credentialsSaving}
                                                            onClick={() => deleteCredential(credential.client_id)}
                                                            aria-label="Delete credential"
                                                        >
                                                            <DeleteOutlined />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="settings-section">
                                        <h4>Add New SP API Credential</h4>

                                        <div className="form-group">
                                            <label htmlFor="new_client_id">Client ID</label>
                                            <input
                                                type="text"
                                                id="new_client_id"
                                                name="client_id"
                                                value={newCredential.client_id}
                                                onChange={handleNewCredentialChange}
                                                placeholder="Enter client ID"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="new_refresh_token">Refresh Token</label>
                                            <input
                                                type="text"
                                                id="new_refresh_token"
                                                name="refresh_token"
                                                value={newCredential.refresh_token}
                                                onChange={handleNewCredentialChange}
                                                placeholder="Enter refresh token"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="new_client_secret">Client Secret</label>
                                            <input
                                                type="text"
                                                id="new_client_secret"
                                                name="client_secret"
                                                value={newCredential.client_secret}
                                                onChange={handleNewCredentialChange}
                                                placeholder="Enter client secret"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="new_seller_id">Seller ID</label>
                                            <input
                                                type="text"
                                                id="new_seller_id"
                                                name="seller_id"
                                                value={newCredential.seller_id}
                                                onChange={handleNewCredentialChange}
                                                placeholder="Enter seller ID"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="new_country">Country</label>
                                            <input
                                                type="text"
                                                id="new_country"
                                                name="country"
                                                value={newCredential.country}
                                                onChange={handleNewCredentialChange}
                                                placeholder="Enter country code (e.g. US)"
                                            />
                                        </div>

                                        <div className="form-group checkbox-group">
                                            <label htmlFor="new_is_default">
                                                <input
                                                    type="checkbox"
                                                    id="new_is_default"
                                                    name="is_default"
                                                    checked={newCredential.is_default}
                                                    onChange={handleNewCredentialChange}
                                                />
                                                Set as default
                                            </label>
                                        </div>

                                        <div className="form-actions">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                disabled={credentialsSaving}
                                                onClick={addCredential}
                                            >
                                                {credentialsSaving ? 'Saving...' : 'Add Credential'}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </Tabs.TabPane>
                </Tabs>
            </div>
        </div>
    );
}

export default Settings;
